import React from 'react';
import { CheckCircle2, Circle, Loader2, ArrowLeft, Repeat, Youtube, XCircle, AlertCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import YouTubePlayer from './YouTubePlayer';
import NovaActionZone from './NovaActionZone';
import VideoValidation from './VideoValidation';

// Barre de progression compacte du dépannage guidé.
export const TroubleshootingProgress = ({ progress = [] }) => {
  if (!progress || progress.length === 0) return null;
  return (
    <div className="px-4 py-2.5 border-b border-[#3A3A3D] bg-[#0A0A0B]" data-testid="ts-progress">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 max-w-3xl mx-auto">
        {progress.map((p, i) => {
          const done = p.state === 'done';
          const current = p.state === 'current';
          return (
            <div key={p.key} className="flex items-center gap-1.5" data-testid="ts-progress-item">
              {done
                ? <CheckCircle2 className="h-3.5 w-3.5 text-[#00FF88] flex-shrink-0" />
                : current
                  ? <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse flex-shrink-0" />
                  : <Circle className="h-3.5 w-3.5 text-[#3A3A3D] flex-shrink-0" />}
              <span className={`text-[11px] ${done ? 'text-[#00FF88]' : current ? 'text-white font-medium' : 'text-[#6B6B6E]'}`}>
                {i + 1}. {p.title}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CtrlButton = ({ onClick, icon: Icon, label, testid }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA] hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors"
    data-testid={testid}
  >
    <Icon className="h-3.5 w-3.5" /> {label}
  </button>
);

// Zone interactive de l'étape courante (réponses rapides + contrôles + action + vidéo).
export const TroubleshootingControls = ({
  session, loading, videos = [], onAnswer, onControl, onVideo, onNavigate, isLoggedIn, onCopyRecap,
}) => {
  if (!session) return null;

  // --- Étape terminale : résolu / partiel / non résolu ---
  if (session.terminal) {
    const resolved = session.outcome === 'resolved';
    const partial = session.outcome === 'partial';
    const recap = session.recap || {};
    return (
      <div className="mt-3 max-w-3xl mx-auto w-full" data-testid="ts-terminal">
        <div className={`rounded-xl border p-3 ${resolved ? 'border-[#00FF88]/30 bg-[#00FF88]/5' : partial ? 'border-[#FFB800]/30 bg-[#FFB800]/5' : 'border-[#FF3B5C]/30 bg-[#FF3B5C]/5'}`}>
          <div className="flex items-center gap-2 mb-2">
            {resolved ? <CheckCircle2 className="h-4 w-4 text-[#00FF88]" />
              : partial ? <AlertCircle className="h-4 w-4 text-[#FFB800]" />
                : <XCircle className="h-4 w-4 text-[#FF3B5C]" />}
            <p className={`text-sm font-semibold ${resolved ? 'text-[#00FF88]' : partial ? 'text-[#FFB800]' : 'text-[#FF3B5C]'}`}>
              {resolved ? 'Problème résolu' : partial ? 'Partiellement résolu' : 'Diagnostic à approfondir'}
            </p>
          </div>
          <div className="text-xs text-[#B8B8BA] space-y-0.5">
            {recap.device && <p><span className="text-[#6B6B6E]">Appareil :</span> {recap.device}</p>}
            {recap.application && <p><span className="text-[#6B6B6E]">Application :</span> {recap.application}</p>}
            {recap.problem && <p><span className="text-[#6B6B6E]">Problème :</span> {recap.problem}</p>}
            {resolved && session.resolution_confirmed_technically && (
              <p className="text-[#00FF88]">✓ Activation confirmée techniquement</p>
            )}
          </div>
          {resolved && (
            <button
              onClick={onCopyRecap}
              className="mt-2 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-white hover:border-[#00FF88] transition-colors"
              data-testid="ts-copy-recap"
            >
              <Copy className="h-3.5 w-3.5" /> Copier le récapitulatif
            </button>
          )}
        </div>
      </div>
    );
  }

  const c = session.controls || {};
  const quickReplies = session.quick_replies || [];

  return (
    <div className="mt-3 max-w-3xl mx-auto w-full" data-testid="ts-controls">
      {/* Carte sécurisée d'identifiants si l'étape le demande */}
      {session.action === 'show_credentials' && (
        <div className="mb-3">
          <NovaActionZone
            action={{ type: 'show_credentials', app: session.app, device: session.device }}
            isLoggedIn={isLoggedIn}
            onNavigate={onNavigate}
          />
        </div>
      )}

      {/* Vidéos proposées */}
      {videos && videos.length > 0 && (
        <div className="mb-3 space-y-3" data-testid="ts-videos">
          {videos.map((v) => (
            <div key={v.video_id}>
              <YouTubePlayer video={v} primary={v.is_primary} onFeedback={() => {}} />
              <VideoValidation videoId={v.video_id} context={{
                device: session.device, application: session.app,
                problem: session.phase || session.problem_category, language: 'fr',
              }} />
            </div>
          ))}
        </div>
      )}

      {/* Réponses rapides de l'étape */}
      {loading ? (
        <div className="flex items-center gap-2 text-[#B8B8BA] text-xs" data-testid="ts-loading">
          <Loader2 className="h-3.5 w-3.5 text-[#FF6B35] animate-spin" /> Nova analyse votre réponse…
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {quickReplies.map((qr, k) => (
            <button
              key={k}
              onClick={() => onAnswer(qr.value, qr.label)}
              className="text-xs px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#E8E8E8] hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors"
              data-testid="ts-quick-reply"
            >
              {qr.label}
            </button>
          ))}
        </div>
      )}

      {/* Contrôles génériques */}
      {!loading && (c.can_go_back || c.can_try_alternative || c.show_video || session.allow_free_text) && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#3A3A3D]/40">
          {c.can_go_back && <CtrlButton onClick={() => onControl('@back', "Revenir en arrière")} icon={ArrowLeft} label="Revenir en arrière" testid="ts-control-back" />}
          {c.can_try_alternative && <CtrlButton onClick={() => onControl('@alternative', "Essayer une autre méthode")} icon={Repeat} label="Essayer une autre méthode" testid="ts-control-alternative" />}
          {c.show_video && <CtrlButton onClick={onVideo} icon={Youtube} label="Regarder la vidéo" testid="ts-control-video" />}
        </div>
      )}
      {session.allow_free_text && !loading && (
        <p className="text-[10px] text-[#6B6B6E] mt-2">Vous pouvez aussi écrire votre réponse dans le champ ci-dessous.</p>
      )}
    </div>
  );
};

export default TroubleshootingControls;
